import {
  Page,
  TextButton,
  VStack,
  Text,
  LoadingIndicator,
} from "@towersystems/roam-common-native";
import { useAuthenticationProvider } from "@towersystems/roam-common-native/lib/utilities/authentication";
import { useCallback, useEffect, useState } from "react";
import { Colors, LoaderScreen, Spacings, View } from "react-native-ui-lib";
import { useDatabase } from "../../utilities/data/hooks";
import FetchMeta from "../../utilities/data/model/FetchMeta";
import { useSetupAllStartupEntities } from "../../utilities/setup";
import { DataError } from "../DataErrorBoundry/DataError";
import { SyncProgressTable } from "./components";
import { AnimatedDots } from "../AnimatedDots";

export interface PrefetchGuardProps {
  children: React.ReactNode | React.ReactNode[] | null;
}

export const PrefetchGuard = ({ children }: PrefetchGuardProps) => {
  const [showAllDetails, setShowAllDetails] = useState(false);

  const database = useDatabase();
  const authentication = useAuthenticationProvider();
  const [prefetchCheckComplete, setPrefetchCheckComplete] = useState(false);
  const [canContinue, setCanContinue] = useState<boolean | undefined>(
    undefined
  );

  const { fetchAndSyncAllEntities, progressState } =
    useSetupAllStartupEntities();

  useEffect(() => {
    fetchAndSyncAllEntities()
      .catch(console.log)
      .finally(() => {
        setPrefetchCheckComplete(true);
      });
  }, []);

  useEffect(() => {
    if (prefetchCheckComplete) {
      database
        .get<FetchMeta>("fetch_meta")
        .query()
        .fetch()
        .then((res) => {
          if (res.every((i) => i.initialSyncOccoured === true)) {
            setCanContinue(true);
          }
        })
        .catch((e) => {
          console.log(e);
          setCanContinue(false);
        });
    }
  }, [prefetchCheckComplete]);

  const resetAndLogoutFn = useCallback(() => {
    database
      .write(async () => {
        return database
          .unsafeResetDatabase()
          .catch((err) => console.log("FAILED TO RESET DB", err));
      })
      .then((i) => {
        authentication.logout();
      });
  }, [authentication, database]);

  const resourcesToDownload = Object.keys(progressState).length;

  const totalComplete = Object.values(progressState).reduce(
    (acc, item) => acc + (item.status === "complete" ? 1 : 0),
    0
  );

  const allComplete =
    Object.values(progressState).length > 0 &&
    Object.values(progressState).every((item) => item.status === "complete");

  if (!prefetchCheckComplete || canContinue === undefined) {
    return (
      <Page scrollable={false}>
        <View
          flex
          style={{
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
          }}
        >
          <VStack centerVertical style={{ width: "100%", maxWidth: 500 }}>
            <View
              style={{
                padding: Spacings.s4,
                alignContent: "center",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {!allComplete && (
                <View
                  style={{
                    position: "absolute",
                    top: -30,
                    left: 0,
                    right: 0,
                    bottom: 0,
                  }}
                >
                  <LoadingIndicator color={Colors.action} />
                </View>
              )}
              <Text variant="heading">{`${totalComplete}\\${resourcesToDownload}`}</Text>
              <AnimatedDots text="Downloading data onto your device"></AnimatedDots>
              <Text
                color="subdued"
                style={{ textAlign: "center", marginTop: Spacings.s2 }}
              >
                This might take a while. Please do not close the app.
              </Text>
              <TextButton
                textStyle={{ alignItems: "center", marginTop: Spacings.s2 }}
                onPress={() => setShowAllDetails(!showAllDetails)}
              >
                {showAllDetails
                  ? "Hide details"
                  : `Show details (${Object.keys(progressState).length})`}
              </TextButton>
            </View>
            {showAllDetails && (
              <SyncProgressTable progressState={progressState} />
            )}
          </VStack>
        </View>
        <View
          style={{
            position: "absolute",
            bottom: Spacings.s4,
            left: Spacings.s4,
            right: Spacings.s4,
            alignItems: "center",
          }}
        >
          <TextButton onPress={resetAndLogoutFn} color="subdued">
            Cancel and logout
          </TextButton>
        </View>
      </Page>
    );
  }

  if (!canContinue) {
    throw new DataError();
  }

  return <>{children}</>;
};
